import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaLeaf,
  FaServer,
  FaGlobe,
  FaRecycle,
  FaSlidersH,
  FaChartBar,
  FaFileAlt,
  FaCopy,
  FaCheck,
  FaDownload,
  FaLightbulb,
  FaBookOpen,
  FaChevronDown,
  FaChevronUp,
  FaTree,
  FaCar,
  FaMobileAlt,
  FaPlane,
  FaInfoCircle,
  FaUser,
  FaBuilding,
  FaCalendarAlt,
  FaTable,
  FaHome,
  FaLaptop,
  FaUsers
} from 'react-icons/fa';

import {
  GRID_CARBON_FACTORS,
  CPU_ARCHITECTURES,
  GREENOPS_PRESETS,
  INDIVIDUAL_PRESETS,
  COMPANY_PRESETS,
  HEATING_FACTORS,
  calculateGreenOpsMetrics,
  calculateIndividualAnnualEmissions,
  calculateCompanyAnnualEmissions,
  generateEsgReport,
  generateIndividualAnnualReport,
  generateCompanyAnnualReport,
  generateCsvExport
} from '../utils/greenOpsCalculations';

const GreenOpsEstimatorPage = () => {
  // Scope Mode: 'cloud' | 'individual' | 'company'
  const [scopeMode, setScopeMode] = useState('cloud');

  // -------------------------------------------------------------
  // CLOUD INFRASTRUCTURE STATE
  // -------------------------------------------------------------
  const [cloudTab, setCloudTab] = useState('calculator'); // 'calculator', 'circular', 'levers', 'report'
  const [cloudConfig, setCloudConfig] = useState(() => GREENOPS_PRESETS[0]);
  const [activeCloudPresetId, setActiveCloudPresetId] = useState('nordic_cleantech');
  const [activeLevers, setActiveLevers] = useState({
    regionShift: false,
    gravitonMigration: false,
    autoShutdown: false,
    lifecycleExtension: false
  });
  const cloudMetrics = useMemo(() => calculateGreenOpsMetrics(cloudConfig, activeLevers), [cloudConfig, activeLevers]);

  // -------------------------------------------------------------
  // INDIVIDUAL ANNUAL STATE (January - December)
  // -------------------------------------------------------------
  const [individualTab, setIndividualTab] = useState('trajectory'); // 'trajectory', 'inputs', 'report'
  const [individualConfig, setIndividualConfig] = useState(() => INDIVIDUAL_PRESETS[0]);
  const [activeIndividualPresetId, setActiveIndividualPresetId] = useState('remote_dev_nordic');
  const individualMetrics = useMemo(() => calculateIndividualAnnualEmissions(individualConfig), [individualConfig]);

  // -------------------------------------------------------------
  // MEDIUM-SIZED COMPANY STATE (January - December)
  // -------------------------------------------------------------
  const [companyTab, setCompanyTab] = useState('trajectory'); // 'trajectory', 'inputs', 'report'
  const [companyConfig, setCompanyConfig] = useState(() => COMPANY_PRESETS[0]);
  const [activeCompanyPresetId, setActiveCompanyPresetId] = useState('remote_tech_startup');
  const companyMetrics = useMemo(() => calculateCompanyAnnualEmissions(companyConfig), [companyConfig]);

  // Shared UI state
  const [copiedReport, setCopiedReport] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [showPrinciples, setShowPrinciples] = useState(false);

  // Apply Cloud Preset
  const handleApplyCloudPreset = (preset) => {
    setActiveCloudPresetId(preset.id);
    setCloudConfig({ ...preset });
    setActiveLevers({
      regionShift: preset.region === 'eu-north-1',
      gravitonMigration: preset.architecture === 'graviton',
      autoShutdown: false,
      lifecycleExtension: preset.serverLifespanYears >= 5
    });
  };

  // Apply Individual Preset
  const handleApplyIndividualPreset = (preset) => {
    setActiveIndividualPresetId(preset.id);
    setIndividualConfig({ ...preset });
  };

  // Apply Company Preset
  const handleApplyCompanyPreset = (preset) => {
    setActiveCompanyPresetId(preset.id);
    setCompanyConfig({ ...preset });
  };

  // Toggle Cloud Lever
  const toggleLever = (key) => {
    setActiveLevers(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Copy Active Report
  const handleCopyReport = () => {
    let reportText = '';
    if (scopeMode === 'cloud') {
      reportText = generateEsgReport(cloudConfig, cloudMetrics, activeLevers);
    } else if (scopeMode === 'individual') {
      reportText = generateIndividualAnnualReport(individualConfig, individualMetrics);
    } else {
      reportText = generateCompanyAnnualReport(companyConfig, companyMetrics);
    }
    navigator.clipboard.writeText(reportText);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2500);
  };

  // Download Active Markdown Report
  const handleDownloadReport = () => {
    let reportText = '';
    let filename = '';

    if (scopeMode === 'cloud') {
      reportText = generateEsgReport(cloudConfig, cloudMetrics, activeLevers);
      filename = `ESG_Cloud_Sustainability_Audit_${cloudConfig.region}.md`;
    } else if (scopeMode === 'individual') {
      reportText = generateIndividualAnnualReport(individualConfig, individualMetrics);
      filename = `Individual_Annual_Carbon_Audit_Jan_Dec_${individualConfig.region}.md`;
    } else {
      reportText = generateCompanyAnnualReport(companyConfig, companyMetrics);
      filename = `Corporate_Annual_CSRD_Carbon_Audit_Jan_Dec_${companyConfig.region}.md`;
    }

    const blob = new Blob([reportText], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download Active CSV Trajectory
  const handleDownloadCsv = () => {
    let csvText = '';
    let filename = '';

    if (scopeMode === 'individual') {
      csvText = generateCsvExport(individualMetrics.monthlyTrajectory, 'Individual Annual Carbon Footprint');
      filename = `Individual_Monthly_Carbon_Jan_Dec_${individualConfig.region}.csv`;
    } else if (scopeMode === 'company') {
      csvText = generateCsvExport(companyMetrics.monthlyTrajectory, `Corporate Annual Carbon Audit (${companyConfig.employeesCount} FTEs)`);
      filename = `Corporate_Monthly_Carbon_Jan_Dec_${companyConfig.region}.csv`;
    } else {
      const rows = cloudMetrics.regionalComparison.map(r => `${r.name},${r.country},${r.carbonIntensity},${r.annualKgCo2e.toFixed(1)},${r.annualTons.toFixed(2)}`);
      csvText = `Region,Country,Grid Intensity (gCO2e/kWh),Annual kg CO2e,Annual Metric Tons\n${rows.join('\n')}`;
      filename = `Cloud_Regional_Carbon_Comparison.csv`;
    }

    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#040d1a',
      color: '#e2e8f0',
      padding: '30px 20px 80px 20px',
      fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        
        {/* Breadcrumb Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2ecc71', fontSize: '0.88rem', fontWeight: 600 }}>
            <Link to="/tools" style={{ color: '#94a3b8', textDecoration: 'none' }}>Tools Hub</Link>
            <span style={{ color: '#64748b' }}>/</span>
            <span style={{ color: '#2ecc71' }}>Sustainability & GreenOps</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Link
              to="/tools/cloud-cost-estimator"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(0, 210, 211, 0.12)',
                border: '1px solid rgba(0, 210, 211, 0.3)',
                borderRadius: '8px',
                padding: '5px 12px',
                fontSize: '0.8rem',
                color: '#00d2d3',
                textDecoration: 'none',
                fontWeight: 600
              }}
            >
              <span>Pair with FinOps Cost Estimator ➔</span>
            </Link>
          </div>
        </div>

        {/* TOP LEVEL ASSESSMENT SCOPE SWITCHER */}
        <div style={{
          background: 'rgba(8, 20, 39, 0.85)',
          border: '1px solid rgba(46, 204, 113, 0.3)',
          borderRadius: '14px',
          padding: '12px 18px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Assessment Scope:
            </span>
          </div>

          <div style={{ display: 'inline-flex', background: 'rgba(15, 23, 42, 0.9)', padding: '4px', borderRadius: '10px', border: '1px solid rgba(148, 163, 184, 0.2)', gap: '4px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setScopeMode('cloud')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: scopeMode === 'cloud' ? 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)' : 'transparent',
                color: scopeMode === 'cloud' ? '#040d1a' : '#cbd5e1',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '0.85rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <FaServer size={14} />
              <span>Cloud & Datacenter Infrastructure</span>
            </button>

            <button
              onClick={() => setScopeMode('individual')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: scopeMode === 'individual' ? 'linear-gradient(135deg, #00d2d3 0%, #0abde3 100%)' : 'transparent',
                color: scopeMode === 'individual' ? '#040d1a' : '#cbd5e1',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '0.85rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <FaUser size={14} />
              <span>Individual Annual Footprint (Jan – Dec)</span>
            </button>

            <button
              onClick={() => setScopeMode('company')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: scopeMode === 'company' ? 'linear-gradient(135deg, #f8d210 0%, #f39c12 100%)' : 'transparent',
                color: scopeMode === 'company' ? '#040d1a' : '#cbd5e1',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '0.85rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <FaBuilding size={14} />
              <span>Medium Company Annual Footprint (Jan – Dec)</span>
            </button>
          </div>
        </div>

        {/* ============================================================= */}
        {/* SCOPE MODE 1: CLOUD & DATACENTER INFRASTRUCTURE               */}
        {/* ============================================================= */}
        {scopeMode === 'cloud' && (
          <>
            {/* Hero Banner */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(8, 38, 28, 0.85) 0%, rgba(4, 15, 30, 0.95) 100%)',
              border: '1px solid rgba(46, 204, 113, 0.35)',
              borderRadius: '16px',
              padding: '24px 28px',
              marginBottom: '26px',
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '20px'
            }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(46, 204, 113, 0.15)', border: '1px solid rgba(46, 204, 113, 0.35)', padding: '4px 12px', borderRadius: '20px', fontSize: '0.78rem', color: '#2ecc71', fontWeight: 700, marginBottom: '8px' }}>
                  <FaLeaf size={13} />
                  <span>M.Sc Circular Economy & Sustainable Cloud Engineering</span>
                </div>
                <h1 style={{ fontSize: '2.1rem', color: '#ffffff', margin: '0 0 8px 0', fontWeight: 700, letterSpacing: '-0.5px' }}>
                  Cloud Carbon & GreenOps Estimator
                </h1>
                <p style={{ fontSize: '0.92rem', color: '#94a3b8', margin: '0 0 16px 0', maxWidth: '720px', lineHeight: '1.5' }}>
                  Quantify Scope 2 grid electricity emissions and Scope 3 hardware embodied carbon across AWS regions using Green Software Foundation (SCI) and GHG Protocol standards.
                </p>

                {/* Guide & Principles Toggles */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setShowGuide(!showGuide)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: showGuide ? 'rgba(46, 204, 113, 0.2)' : 'rgba(15, 23, 42, 0.7)',
                      border: `1px solid ${showGuide ? '#2ecc71' : 'rgba(46, 204, 113, 0.3)'}`,
                      color: showGuide ? '#2ecc71' : '#e2e8f0',
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FaBookOpen size={12} />
                    <span>{showGuide ? 'Hide User Guide' : 'How to Use Guide'}</span>
                    {showGuide ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                  </button>

                  <button
                    onClick={() => setShowPrinciples(!showPrinciples)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: showPrinciples ? 'rgba(248, 210, 16, 0.2)' : 'rgba(15, 23, 42, 0.7)',
                      border: `1px solid ${showPrinciples ? '#f8d210' : 'rgba(248, 210, 16, 0.3)'}`,
                      color: showPrinciples ? '#f8d210' : '#e2e8f0',
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <FaLightbulb size={12} color="#f8d210" />
                    <span>{showPrinciples ? 'Hide Circular Principles' : 'Circular Economy & Green Cloud Principles'}</span>
                    {showPrinciples ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                  </button>
                </div>
              </div>

              {/* Carbon Footprint KPI Block */}
              <div style={{
                background: 'rgba(6, 24, 18, 0.9)',
                border: '1px solid rgba(46, 204, 113, 0.4)',
                borderRadius: '12px',
                padding: '18px 24px',
                textAlign: 'right',
                minWidth: '240px'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
                  Estimated Cloud Footprint
                </div>
                <div style={{ fontSize: '2rem', color: '#2ecc71', fontWeight: 800, fontFamily: 'monospace', margin: '4px 0' }}>
                  {cloudMetrics.emissions.totalMonthlyKgCo2e.toFixed(1)}
                  <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 400 }}> kg CO₂e /mo</span>
                </div>
                <div style={{ fontSize: '0.82rem', color: '#00d2d3', fontWeight: 700 }}>
                  {cloudMetrics.emissions.totalAnnualMetricTons.toFixed(2)} Metric Tons CO₂e / year
                </div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px' }}>
                  Grid Rating: <strong style={{ color: cloudMetrics.region.color }}>{cloudMetrics.region.rating}</strong>
                </div>
              </div>
            </div>

            {/* COLLAPSIBLE HOW TO USE GUIDE */}
            {showGuide && (
              <div style={{
                background: 'rgba(8, 20, 39, 0.85)',
                border: '1px solid rgba(46, 204, 113, 0.3)',
                borderRadius: '14px',
                padding: '20px 24px',
                marginBottom: '26px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
                animation: 'fadeIn 0.3s ease-in-out'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(46, 204, 113, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2ecc71' }}>
                      <FaBookOpen size={16} />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#f8fafc', fontWeight: 700 }}>
                        How to Size and Decarbonize Your Cloud Workloads
                      </h3>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                        Follow this 4-step GreenOps framework to quantify emissions, amortize embodied carbon, and generate CSRD sustainability reports.
                      </p>
                    </div>
                  </div>
                  <span style={{ fontSize: '0.75rem', background: 'rgba(46, 204, 113, 0.15)', color: '#2ecc71', padding: '3px 10px', borderRadius: '12px', fontWeight: 700 }}>
                    GreenOps Workflow
                  </span>
                </div>

                {/* 4 Step Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '14px', marginBottom: '16px' }}>
                  <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '10px', padding: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#2ecc71', color: '#040d1a', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2ecc71' }}>Select Archetype or Profile</span>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                      Choose from 4 pre-configured presets below (e.g. <em>Nordic Clean Tech</em>, <em>AI Cluster</em>, or <em>Legacy On-Prem Migration</em>) to load real-world CPU and storage baselines.
                    </p>
                  </div>

                  <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '10px', padding: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#00d2d3', color: '#040d1a', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#00d2d3' }}>Audit Regional Grid Factor</span>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                      Inspect how grid carbon intensity varies from <strong>24 gCO₂e/kWh (Nordics)</strong> up to <strong>708 gCO₂e/kWh (Coal-heavy grids)</strong> on the multi-region bar chart.
                    </p>
                  </div>

                  <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '10px', padding: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#f8d210', color: '#040d1a', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#f8d210' }}>Apply GreenOps Levers</span>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                      In the <strong>GreenOps Levers</strong> tab, toggle Region Shifting, Graviton ARM64 migration, and Auto-Shutdown to measure real-time carbon avoidance percentages.
                    </p>
                  </div>

                  <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '10px', padding: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#a29bfe', color: '#040d1a', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>4</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#a29bfe' }}>Export ESG Audit Report</span>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                      Export compliant Markdown reports formatted for <strong>GHG Protocol Scope 2/3</strong> and Corporate Sustainability Reporting Directive (CSRD) stakeholder reviews.
                    </p>
                  </div>
                </div>

                {/* Pro Tip Callout */}
                <div style={{
                  background: 'rgba(46, 204, 113, 0.08)',
                  borderLeft: '4px solid #2ecc71',
                  borderRadius: '0 8px 8px 0',
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '0.78rem',
                  color: '#cbd5e1'
                }}>
                  <FaInfoCircle color="#2ecc71" size={16} style={{ flexShrink: 0 }} />
                  <span>
                    <strong>Circular Economy Insight:</strong> Migrating identical workloads from a standard coal/gas region (e.g. US East at 379 gCO₂e/kWh) to <strong>Nordic Hydro/Nuclear (eu-north-1 at 24 gCO₂e/kWh)</strong> immediately cuts operational carbon by <strong>over 93% without altering code</strong>.
                  </span>
                </div>
              </div>
            )}

            {/* COLLAPSIBLE CIRCULAR PRINCIPLES */}
            {showPrinciples && (
              <div style={{
                background: 'rgba(8, 20, 39, 0.85)',
                border: '1px solid rgba(248, 210, 16, 0.3)',
                borderRadius: '14px',
                padding: '20px 24px',
                marginBottom: '26px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
                animation: 'fadeIn 0.3s ease-in-out'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(248, 210, 16, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f8d210' }}>
                      <FaRecycle size={16} />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#f8fafc', fontWeight: 700 }}>
                        Circular Economy & Sustainable Cloud Computing Principles
                      </h3>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                        Synthesized from Adedeji's M.Sc in Circular Economy and Green Software Foundation methodologies.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPrinciples(false)}
                    style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.8rem' }}
                  >
                    ✕ Close
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' }}>
                  <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(248, 210, 16, 0.15)' }}>
                    <div style={{ color: '#f8d210', fontWeight: 700, fontSize: '0.85rem', marginBottom: '4px' }}>
                      🔄 Hardware Lifespan & Scope 3 Amortization
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.4' }}>
                      Server fabrication generates ~1,200 kg CO₂e in cradle-to-gate embodied carbon. Extending useful server lifespan from 3 to 5 years reduces annualized embodied carbon emissions by 40%.
                    </div>
                  </div>

                  <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(248, 210, 16, 0.15)' }}>
                    <div style={{ color: '#f8d210', fontWeight: 700, fontSize: '0.85rem', marginBottom: '4px' }}>
                      ⚡ ARM64 Silicon Energy Efficiency
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.4' }}>
                      AWS Graviton processors achieve 3.3W active draw per vCPU compared to ~8.2W on legacy x86 architectures, achieving ~60% reduction in electricity demand for identical throughput.
                    </div>
                  </div>

                  <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(248, 210, 16, 0.15)' }}>
                    <div style={{ color: '#f8d210', fontWeight: 700, fontSize: '0.85rem', marginBottom: '4px' }}>
                      🌍 Spatial Shifting (Carbon-Aware Location)
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.4' }}>
                      Not all workloads require low-latency placement next to end users. Batch processing, backups, and AI model training can be spatially relocated to zero-carbon hydro/geothermal regions.
                    </div>
                  </div>

                  <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(248, 210, 16, 0.15)' }}>
                    <div style={{ color: '#f8d210', fontWeight: 700, fontSize: '0.85rem', marginBottom: '4px' }}>
                      ⏱️ Temporal Shifting (Time-Aware Scheduling)
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.4' }}>
                      Scheduling ETL pipelines and non-urgent batch analytics during solar and wind generation peaks aligns workload execution with minimum marginal grid carbon intensity.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Presets */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaLightbulb color="#f8d210" size={13} />
                <span>Sustainability Archetype Presets</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
                {GREENOPS_PRESETS.map(preset => {
                  const isSelected = activeCloudPresetId === preset.id;
                  return (
                    <div
                      key={preset.id}
                      onClick={() => handleApplyCloudPreset(preset)}
                      style={{
                        backgroundColor: isSelected ? 'rgba(46, 204, 113, 0.12)' : 'rgba(8, 20, 39, 0.65)',
                        border: `1px solid ${isSelected ? '#2ecc71' : 'rgba(148, 163, 184, 0.15)'}`,
                        borderRadius: '10px',
                        padding: '12px 14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '0.88rem', fontWeight: 700, color: isSelected ? '#2ecc71' : '#f8fafc' }}>
                          {preset.name}
                        </span>
                        <span style={{
                          fontSize: '0.68rem',
                          background: isSelected ? '#2ecc71' : 'rgba(148, 163, 184, 0.15)',
                          color: isSelected ? '#040d1a' : '#94a3b8',
                          fontWeight: 700,
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          {preset.badge}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.76rem', color: '#94a3b8', margin: 0, lineHeight: '1.35' }}>
                        {preset.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Cloud Tabs Navigation */}
            <div style={{
              display: 'flex',
              gap: '8px',
              borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
              marginBottom: '24px',
              overflowX: 'auto',
              paddingBottom: '4px'
            }}>
              {[
                { id: 'calculator', label: 'Cloud Carbon Footprint & Multi-Region', icon: <FaChartBar /> },
                { id: 'circular', label: 'Circular Economy & Embodied Carbon', icon: <FaRecycle /> },
                { id: 'levers', label: 'GreenOps Levers & Net-Zero Plan', icon: <FaSlidersH /> },
                { id: 'report', label: 'ESG Sustainability Audit Report', icon: <FaFileAlt /> }
              ].map(tab => {
                const isActive = cloudTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setCloudTab(tab.id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: isActive ? 'rgba(46, 204, 113, 0.15)' : 'transparent',
                      border: 'none',
                      borderBottom: `2px solid ${isActive ? '#2ecc71' : 'transparent'}`,
                      padding: '10px 18px',
                      color: isActive ? '#2ecc71' : '#94a3b8',
                      fontSize: '0.88rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Cloud Tab 1: Calculator */}
            {cloudTab === 'calculator' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                {/* Left: Sizing Inputs */}
                <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                  <h3 style={{ fontSize: '1rem', color: '#f8fafc', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaSlidersH color="#2ecc71" />
                    <span>Workload & Datacenter Sizing</span>
                  </h3>

                  {/* Region Selector */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#cbd5e1', fontWeight: 600, marginBottom: '6px' }}>
                      Primary Cloud Region (Grid Carbon Intensity)
                    </label>
                    <select
                      value={cloudConfig.region}
                      onChange={(e) => setCloudConfig({ ...cloudConfig, region: e.target.value })}
                      style={{
                        width: '100%',
                        backgroundColor: '#0f172a',
                        border: '1px solid rgba(148, 163, 184, 0.25)',
                        color: '#f8fafc',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        fontSize: '0.85rem'
                      }}
                    >
                      {GRID_CARBON_FACTORS.map(r => (
                        <option key={r.id} value={r.id}>
                          {r.name} — {r.carbonIntensity} gCO₂e/kWh ({r.rating})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* CPU Architecture */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#cbd5e1', fontWeight: 600, marginBottom: '6px' }}>
                      Processor Silicon Architecture
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {Object.values(CPU_ARCHITECTURES).map(arch => {
                        const isArchSelected = cloudConfig.architecture === arch.id;
                        return (
                          <div
                            key={arch.id}
                            onClick={() => setCloudConfig({ ...cloudConfig, architecture: arch.id })}
                            style={{
                              backgroundColor: isArchSelected ? 'rgba(46, 204, 113, 0.15)' : 'rgba(15, 23, 42, 0.8)',
                              border: `1px solid ${isArchSelected ? '#2ecc71' : 'rgba(148, 163, 184, 0.2)'}`,
                              borderRadius: '8px',
                              padding: '10px',
                              cursor: 'pointer',
                              textAlign: 'center'
                            }}
                          >
                            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: isArchSelected ? '#2ecc71' : '#e2e8f0' }}>
                              {arch.name}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '2px' }}>
                              {arch.activeWattsPerVcpu}W active / vCPU
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* vCPU Slider */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Virtual CPUs (vCPUs)</span>
                      <strong style={{ color: '#2ecc71' }}>{cloudConfig.vcpus} vCPUs</strong>
                    </div>
                    <input
                      type="range"
                      min="2"
                      max="256"
                      step="2"
                      value={cloudConfig.vcpus}
                      onChange={(e) => setCloudConfig({ ...cloudConfig, vcpus: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#2ecc71' }}
                    />
                  </div>

                  {/* RAM Slider */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Memory (RAM)</span>
                      <strong style={{ color: '#00d2d3' }}>{cloudConfig.ramGb} GB</strong>
                    </div>
                    <input
                      type="range"
                      min="4"
                      max="1024"
                      step="4"
                      value={cloudConfig.ramGb}
                      onChange={(e) => setCloudConfig({ ...cloudConfig, ramGb: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#00d2d3' }}
                    />
                  </div>

                  {/* Storage Slider */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Storage Capacity</span>
                      <strong style={{ color: '#f8d210' }}>{cloudConfig.storageTb} TB ({cloudConfig.storageType.toUpperCase()})</strong>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="100"
                      step="1"
                      value={cloudConfig.storageTb}
                      onChange={(e) => setCloudConfig({ ...cloudConfig, storageTb: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#f8d210' }}
                    />
                  </div>

                  {/* Average CPU Utilization */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Average Compute Utilization</span>
                      <strong style={{ color: '#a29bfe' }}>{cloudConfig.avgUtilizationPercent}%</strong>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="5"
                      value={cloudConfig.avgUtilizationPercent}
                      onChange={(e) => setCloudConfig({ ...cloudConfig, avgUtilizationPercent: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#a29bfe' }}
                    />
                  </div>
                </div>

                {/* Right: Real-World Equivalence & Regional Bar Chart */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Real World Equivalences */}
                  <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                    <h3 style={{ fontSize: '1rem', color: '#f8fafc', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaTree color="#2ecc71" />
                      <span>Tangible Real-World Equivalents (Annualized)</span>
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div style={{ background: 'rgba(15, 23, 42, 0.8)', borderRadius: '10px', padding: '12px', border: '1px solid rgba(46, 204, 113, 0.2)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2ecc71', marginBottom: '4px' }}>
                          <FaTree size={16} />
                          <span style={{ fontSize: '0.76rem', fontWeight: 600 }}>Trees Needed (10 Yrs)</span>
                        </div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f8fafc' }}>
                          {Math.round(cloudMetrics.equivalents.treeSeedlingsNeeded)}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>seedlings sequestering CO₂</div>
                      </div>

                      <div style={{ background: 'rgba(15, 23, 42, 0.8)', borderRadius: '10px', padding: '12px', border: '1px solid rgba(0, 210, 211, 0.2)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#00d2d3', marginBottom: '4px' }}>
                          <FaCar size={16} />
                          <span style={{ fontSize: '0.76rem', fontWeight: 600 }}>Gasoline Car Miles</span>
                        </div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f8fafc' }}>
                          {Math.round(cloudMetrics.equivalents.milesDrivenAvoided).toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>passenger vehicle miles</div>
                      </div>

                      <div style={{ background: 'rgba(15, 23, 42, 0.8)', borderRadius: '10px', padding: '12px', border: '1px solid rgba(248, 210, 16, 0.2)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f8d210', marginBottom: '4px' }}>
                          <FaMobileAlt size={16} />
                          <span style={{ fontSize: '0.76rem', fontWeight: 600 }}>Smartphones Charged</span>
                        </div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f8fafc' }}>
                          {Math.round(cloudMetrics.equivalents.smartphonesCharged).toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>full battery cycles</div>
                      </div>

                      <div style={{ background: 'rgba(15, 23, 42, 0.8)', borderRadius: '10px', padding: '12px', border: '1px solid rgba(255, 118, 117, 0.2)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ff7675', marginBottom: '4px' }}>
                          <FaPlane size={16} />
                          <span style={{ fontSize: '0.76rem', fontWeight: 600 }}>Flight Equivalents</span>
                        </div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#f8fafc' }}>
                          {cloudMetrics.equivalents.flightsEquivalent.toFixed(1)}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>transatlantic passenger trips</div>
                      </div>
                    </div>
                  </div>

                  {/* Multi-Region Horizontal Bar Chart */}
                  <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                    <h3 style={{ fontSize: '1rem', color: '#f8fafc', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaGlobe color="#00d2d3" />
                      <span>Multi-Region Grid Carbon Intensity Audit</span>
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {cloudMetrics.regionalComparison.map(reg => {
                        const maxVal = 708;
                        const pct = Math.max(5, (reg.carbonIntensity / maxVal) * 100);
                        return (
                          <div key={reg.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.78rem' }}>
                            <div style={{ width: '150px', color: reg.isCurrent ? '#2ecc71' : '#cbd5e1', fontWeight: reg.isCurrent ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {reg.name}
                            </div>
                            <div style={{ flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.8)', height: '14px', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', backgroundColor: reg.color, borderRadius: '4px', transition: 'width 0.4s ease' }} />
                            </div>
                            <div style={{ width: '80px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: reg.color }}>
                              {reg.carbonIntensity} <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>g/kWh</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cloud Tab 2: Circular Economy */}
            {cloudTab === 'circular' && (
              <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '24px' }}>
                <h3 style={{ fontSize: '1.1rem', color: '#f8fafc', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaRecycle color="#2ecc71" />
                  <span>Scope 2 Operational Electricity vs. Scope 3 Embodied Carbon</span>
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                  <div style={{ background: 'rgba(15, 23, 42, 0.8)', borderRadius: '12px', padding: '18px', border: '1px solid rgba(46, 204, 113, 0.2)' }}>
                    <div style={{ fontSize: '0.78rem', color: '#2ecc71', fontWeight: 700, textTransform: 'uppercase' }}>Scope 2 Operational Emissions</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#f8fafc', margin: '6px 0' }}>
                      {(cloudMetrics.emissions.annualOperationalKgCo2e / 1000).toFixed(2)}
                      <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 400 }}> tCO₂e / yr</span>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                      Emissions stemming from datacenter grid electricity to power CPUs, memory, cooling, and facility infrastructure.
                    </p>
                  </div>

                  <div style={{ background: 'rgba(15, 23, 42, 0.8)', borderRadius: '12px', padding: '18px', border: '1px solid rgba(0, 210, 211, 0.2)' }}>
                    <div style={{ fontSize: '0.78rem', color: '#00d2d3', fontWeight: 700, textTransform: 'uppercase' }}>Scope 3 Embodied Carbon</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#f8fafc', margin: '6px 0' }}>
                      {(cloudMetrics.emissions.annualEmbodiedKgCo2e / 1000).toFixed(2)}
                      <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 400 }}> tCO₂e / yr</span>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                      Cradle-to-gate server manufacturing, silicon fabrication, transportation, and lifecycle replacement amortization.
                    </p>
                  </div>
                </div>

                {/* Server Lifespan Extension Slider */}
                <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '16px', border: '1px solid rgba(148, 163, 184, 0.2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#cbd5e1', marginBottom: '8px' }}>
                    <span>Server Hardware Lifespan (Circular Economy Amortization)</span>
                    <strong style={{ color: '#2ecc71' }}>{cloudConfig.serverLifespanYears} Years</strong>
                  </div>
                  <input
                    type="range"
                    min="3"
                    max="6"
                    step="1"
                    value={cloudConfig.serverLifespanYears}
                    onChange={(e) => setCloudConfig({ ...cloudConfig, serverLifespanYears: Number(e.target.value) })}
                    style={{ width: '100%', accentColor: '#2ecc71' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>
                    <span>3 Years (Traditional Refresh)</span>
                    <span>4 Years (Industry Average)</span>
                    <span>5 Years (Circular Target)</span>
                    <span>6 Years (Maximum Longevity)</span>
                  </div>
                </div>
              </div>
            )}

            {/* Cloud Tab 3: Levers */}
            {cloudTab === 'levers' && (
              <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '24px' }}>
                <h3 style={{ fontSize: '1.1rem', color: '#f8fafc', marginBottom: '16px', display: 'center', gap: '8px' }}>
                  <FaSlidersH color="#2ecc71" />
                  <span>GreenOps Optimization Levers & Net-Zero Avoided Carbon</span>
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px', marginBottom: '24px' }}>
                  <div
                    onClick={() => toggleLever('regionShift')}
                    style={{
                      background: activeLevers.regionShift ? 'rgba(46, 204, 113, 0.15)' : 'rgba(15, 23, 42, 0.8)',
                      border: `1px solid ${activeLevers.regionShift ? '#2ecc71' : 'rgba(148, 163, 184, 0.2)'}`,
                      borderRadius: '10px',
                      padding: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontWeight: 700, color: activeLevers.regionShift ? '#2ecc71' : '#f8fafc' }}>
                        🌍 Spatial Region Shifting
                      </span>
                      <input type="checkbox" checked={activeLevers.regionShift} readOnly />
                    </div>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                      Shift workloads to the Nordic clean hydro/nuclear grid (Stockholm/Helsinki at 24 gCO₂e/kWh).
                    </p>
                  </div>

                  <div
                    onClick={() => toggleLever('gravitonMigration')}
                    style={{
                      background: activeLevers.gravitonMigration ? 'rgba(0, 210, 211, 0.15)' : 'rgba(15, 23, 42, 0.8)',
                      border: `1px solid ${activeLevers.gravitonMigration ? '#00d2d3' : 'rgba(148, 163, 184, 0.2)'}`,
                      borderRadius: '10px',
                      padding: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontWeight: 700, color: activeLevers.gravitonMigration ? '#00d2d3' : '#f8fafc' }}>
                        ⚡ AWS Graviton Modernization
                      </span>
                      <input type="checkbox" checked={activeLevers.gravitonMigration} readOnly />
                    </div>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                      Migrate compute to ARM64 Graviton architecture to cut active compute power from 8.2W down to 3.3W/vCPU (-60%).
                    </p>
                  </div>

                  <div
                    onClick={() => toggleLever('autoShutdown')}
                    style={{
                      background: activeLevers.autoShutdown ? 'rgba(248, 210, 16, 0.15)' : 'rgba(15, 23, 42, 0.8)',
                      border: `1px solid ${activeLevers.autoShutdown ? '#f8d210' : 'rgba(148, 163, 184, 0.2)'}`,
                      borderRadius: '10px',
                      padding: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontWeight: 700, color: activeLevers.autoShutdown ? '#f8d210' : '#f8fafc' }}>
                        ⏱️ Non-Prod Auto-Shutdown
                      </span>
                      <input type="checkbox" checked={activeLevers.autoShutdown} readOnly />
                    </div>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                      Power down staging & dev instances outside business hours (saves ~65% of monthly operating hours).
                    </p>
                  </div>

                  <div
                    onClick={() => toggleLever('lifecycleExtension')}
                    style={{
                      background: activeLevers.lifecycleExtension ? 'rgba(162, 155, 254, 0.15)' : 'rgba(15, 23, 42, 0.8)',
                      border: `1px solid ${activeLevers.lifecycleExtension ? '#a29bfe' : 'rgba(148, 163, 184, 0.2)'}`,
                      borderRadius: '10px',
                      padding: '16px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontWeight: 700, color: activeLevers.lifecycleExtension ? '#a29bfe' : '#f8fafc' }}>
                        🔄 5-Year Circular Lifecycle
                      </span>
                      <input type="checkbox" checked={activeLevers.lifecycleExtension} readOnly />
                    </div>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                      Amortize server hardware manufacturing over 5 years instead of 3 years to reduce embodied carbon.
                    </p>
                  </div>
                </div>

                {/* Net-Zero Scorecard */}
                <div style={{ background: 'rgba(15, 23, 42, 0.8)', borderRadius: '12px', padding: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px', border: '1px solid rgba(46, 204, 113, 0.3)' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Total Annual Carbon Avoided</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#2ecc71', fontFamily: 'monospace' }}>
                      {(cloudMetrics.benchmarks.annualCarbonAvoidedKg / 1000).toFixed(2)} Metric Tons CO₂e
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700 }}>Net Reduction Rate</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#00d2d3', fontFamily: 'monospace' }}>
                      -{cloudMetrics.benchmarks.reductionPercentage.toFixed(1)}%
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cloud Tab 4: Report */}
            {cloudTab === 'report' && (
              <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h3 style={{ fontSize: '1.1rem', color: '#f8fafc', margin: '0 0 4px 0' }}>
                      Corporate ESG Sustainability & GHG Protocol Audit Report
                    </h3>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0 }}>
                      Formatted for board presentations, CSRD compliance, and stakeholder disclosures.
                    </p>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                      onClick={handleCopyReport}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: copiedReport ? 'rgba(46, 204, 113, 0.2)' : 'rgba(15, 23, 42, 0.8)',
                        border: `1px solid ${copiedReport ? '#2ecc71' : 'rgba(148, 163, 184, 0.3)'}`,
                        color: copiedReport ? '#2ecc71' : '#e2e8f0',
                        borderRadius: '8px',
                        padding: '7px 14px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      {copiedReport ? <FaCheck /> : <FaCopy />}
                      <span>{copiedReport ? 'Copied!' : 'Copy Report'}</span>
                    </button>

                    <button
                      onClick={handleDownloadReport}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                        color: '#040d1a',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '7px 16px',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      <FaDownload />
                      <span>Download Audit (.md)</span>
                    </button>
                  </div>
                </div>

                <pre style={{
                  backgroundColor: '#030a14',
                  border: '1px solid rgba(148, 163, 184, 0.15)',
                  borderRadius: '10px',
                  padding: '20px',
                  color: '#cbd5e1',
                  fontFamily: 'monospace',
                  fontSize: '0.82rem',
                  lineHeight: '1.5',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap'
                }}>
                  {generateEsgReport(cloudConfig, cloudMetrics, activeLevers)}
                </pre>
              </div>
            )}
          </>
        )}

        {/* ============================================================= */}
        {/* SCOPE MODE 2: INDIVIDUAL ANNUAL FOOTPRINT (Jan - Dec)         */}
        {/* ============================================================= */}
        {scopeMode === 'individual' && (
          <>
            {/* Hero Banner for Individual Mode */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(4, 25, 40, 0.9) 0%, rgba(4, 15, 30, 0.95) 100%)',
              border: '1px solid rgba(0, 210, 211, 0.35)',
              borderRadius: '16px',
              padding: '24px 28px',
              marginBottom: '26px',
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '20px'
            }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(0, 210, 211, 0.15)', border: '1px solid rgba(0, 210, 211, 0.35)', padding: '4px 12px', borderRadius: '20px', fontSize: '0.78rem', color: '#00d2d3', fontWeight: 700, marginBottom: '8px' }}>
                  <FaUser size={13} />
                  <span>Individual Annual Footprint • January – December Cycle</span>
                </div>
                <h1 style={{ fontSize: '2.1rem', color: '#ffffff', margin: '0 0 8px 0', fontWeight: 700, letterSpacing: '-0.5px' }}>
                  Individual Annual Carbon Accounting
                </h1>
                <p style={{ fontSize: '0.92rem', color: '#94a3b8', margin: 0, maxWidth: '720px', lineHeight: '1.5' }}>
                  Annualized personal carbon audit modeling home office electricity, space heating, digital workstation hardware, cloud compute, and mobility travel across all 12 calendar months.
                </p>
              </div>

              {/* Individual Annual Footprint KPI Block */}
              <div style={{
                background: 'rgba(4, 20, 30, 0.9)',
                border: '1px solid rgba(0, 210, 211, 0.4)',
                borderRadius: '12px',
                padding: '18px 24px',
                textAlign: 'right',
                minWidth: '240px'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
                  Annual Footprint (Jan – Dec)
                </div>
                <div style={{ fontSize: '2rem', color: '#00d2d3', fontWeight: 800, fontFamily: 'monospace', margin: '4px 0' }}>
                  {individualMetrics.totals.totalAnnualTons.toFixed(2)}
                  <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 400 }}> tCO₂e / yr</span>
                </div>
                <div style={{ fontSize: '0.82rem', color: individualMetrics.totals.totalAnnualTons <= 2.0 ? '#2ecc71' : '#f8d210', fontWeight: 700 }}>
                  {individualMetrics.totals.dailyAverageKg.toFixed(1)} kg CO₂e / day
                </div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px' }}>
                  Target: <strong>2.0 tCO₂e (Paris 2030)</strong>
                </div>
              </div>
            </div>

            {/* Individual Presets */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaLightbulb color="#00d2d3" size={13} />
                <span>Select Professional Lifestyle Archetype</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
                {INDIVIDUAL_PRESETS.map(preset => {
                  const isSelected = activeIndividualPresetId === preset.id;
                  return (
                    <div
                      key={preset.id}
                      onClick={() => handleApplyIndividualPreset(preset)}
                      style={{
                        backgroundColor: isSelected ? 'rgba(0, 210, 211, 0.12)' : 'rgba(8, 20, 39, 0.65)',
                        border: `1px solid ${isSelected ? '#00d2d3' : 'rgba(148, 163, 184, 0.15)'}`,
                        borderRadius: '10px',
                        padding: '12px 14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '0.88rem', fontWeight: 700, color: isSelected ? '#00d2d3' : '#f8fafc' }}>
                          {preset.name}
                        </span>
                        <span style={{
                          fontSize: '0.68rem',
                          background: isSelected ? '#00d2d3' : 'rgba(148, 163, 184, 0.15)',
                          color: isSelected ? '#040d1a' : '#94a3b8',
                          fontWeight: 700,
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          {preset.badge}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.76rem', color: '#94a3b8', margin: 0, lineHeight: '1.35' }}>
                        {preset.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Individual Sub-Tabs Navigation */}
            <div style={{
              display: 'flex',
              gap: '8px',
              borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
              marginBottom: '24px',
              overflowX: 'auto',
              paddingBottom: '4px'
            }}>
              {[
                { id: 'trajectory', label: '12-Month Calendar Trajectory (Jan – Dec)', icon: <FaCalendarAlt /> },
                { id: 'inputs', label: 'Workstation & Lifestyle Sizing Parameters', icon: <FaSlidersH /> },
                { id: 'report', label: 'Comprehensive Annual Audit Report', icon: <FaFileAlt /> }
              ].map(tab => {
                const isActive = individualTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setIndividualTab(tab.id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: isActive ? 'rgba(0, 210, 211, 0.15)' : 'transparent',
                      border: 'none',
                      borderBottom: `2px solid ${isActive ? '#00d2d3' : 'transparent'}`,
                      padding: '10px 18px',
                      color: isActive ? '#00d2d3' : '#94a3b8',
                      fontSize: '0.88rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Individual Tab 1: Trajectory */}
            {individualTab === 'trajectory' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* 12-Month Monthly Bar Visualizer */}
                <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ fontSize: '1.05rem', color: '#f8fafc', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaChartBar color="#00d2d3" />
                      <span>January – December Monthly Emissions Profile</span>
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.75rem' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#e67e22' }}>● Scope 1</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#00d2d3' }}>● Scope 2</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#a29bfe' }}>● Scope 3</span>
                    </div>
                  </div>

                  {/* 12 Bar Columns */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '8px', alignItems: 'flex-end', height: '180px', paddingTop: '20px', borderBottom: '1px solid rgba(148, 163, 184, 0.2)' }}>
                    {individualMetrics.monthlyTrajectory.map(m => {
                      const maxMonthKg = 600; // Normalizing scale
                      const barHeight = Math.min(100, Math.max(10, (m.totalKg / maxMonthKg) * 100));
                      return (
                        <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                          <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginBottom: '4px', fontFamily: 'monospace' }}>
                            {Math.round(m.totalKg)}
                          </div>
                          <div style={{
                            width: '100%',
                            height: `${barHeight}%`,
                            background: 'linear-gradient(180deg, #00d2d3 0%, #0984e3 100%)',
                            borderRadius: '4px 4px 0 0',
                            transition: 'height 0.3s ease'
                          }} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Month Labels */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '8px', marginTop: '8px', textAlign: 'center' }}>
                    {individualMetrics.monthlyTrajectory.map(m => (
                      <div key={m.id} style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>
                        {m.short}
                      </div>
                    ))}
                  </div>
                </div>

                {/* 12-Month Detailed Data Table */}
                <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ fontSize: '1rem', color: '#f8fafc', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaTable color="#00d2d3" />
                      <span>Itemized 12-Month Calendar Breakdown (kg CO₂e)</span>
                    </h3>
                    <button
                      onClick={handleDownloadCsv}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'rgba(0, 210, 211, 0.15)',
                        border: '1px solid rgba(0, 210, 211, 0.35)',
                        color: '#00d2d3',
                        borderRadius: '6px',
                        padding: '5px 12px',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      <FaDownload size={11} />
                      <span>Download CSV</span>
                    </button>
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.2)', color: '#94a3b8' }}>
                          <th style={{ padding: '8px 10px' }}>Month</th>
                          <th style={{ padding: '8px 10px' }}>Season</th>
                          <th style={{ padding: '8px 10px' }}>Electricity</th>
                          <th style={{ padding: '8px 10px' }}>Heating</th>
                          <th style={{ padding: '8px 10px' }}>Transport</th>
                          <th style={{ padding: '8px 10px' }}>Digital/HW</th>
                          <th style={{ padding: '8px 10px' }}>Scope 1</th>
                          <th style={{ padding: '8px 10px' }}>Scope 2</th>
                          <th style={{ padding: '8px 10px' }}>Scope 3</th>
                          <th style={{ padding: '8px 10px', color: '#00d2d3' }}>Total (kg)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {individualMetrics.monthlyTrajectory.map((m, idx) => (
                          <tr key={m.id} style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.1)', backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(15, 23, 42, 0.4)' }}>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: '#f8fafc' }}>{m.name}</td>
                            <td style={{ padding: '8px 10px', color: '#94a3b8' }}>{m.season}</td>
                            <td style={{ padding: '8px 10px' }}>{m.electricityKg.toFixed(1)}</td>
                            <td style={{ padding: '8px 10px' }}>{m.heatingKg.toFixed(1)}</td>
                            <td style={{ padding: '8px 10px' }}>{(m.carKg + m.transitAndFlightsKg).toFixed(1)}</td>
                            <td style={{ padding: '8px 10px' }}>{m.digitalAndHardwareKg.toFixed(1)}</td>
                            <td style={{ padding: '8px 10px', color: '#e67e22' }}>{m.scope1.toFixed(1)}</td>
                            <td style={{ padding: '8px 10px', color: '#00d2d3' }}>{m.scope2.toFixed(1)}</td>
                            <td style={{ padding: '8px 10px', color: '#a29bfe' }}>{m.scope3.toFixed(1)}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: '#00d2d3', fontFamily: 'monospace' }}>{m.totalKg.toFixed(1)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Individual Tab 2: Inputs */}
            {individualTab === 'inputs' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                {/* Home Office & Domestic Energy */}
                <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                  <h3 style={{ fontSize: '1rem', color: '#f8fafc', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaHome color="#00d2d3" />
                    <span>Home Office & Residential Energy</span>
                  </h3>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#cbd5e1', fontWeight: 600, marginBottom: '6px' }}>
                      Residential Location (Grid Factor)
                    </label>
                    <select
                      value={individualConfig.region}
                      onChange={(e) => setIndividualConfig({ ...individualConfig, region: e.target.value })}
                      style={{
                        width: '100%',
                        backgroundColor: '#0f172a',
                        border: '1px solid rgba(148, 163, 184, 0.25)',
                        color: '#f8fafc',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        fontSize: '0.85rem'
                      }}
                    >
                      {GRID_CARBON_FACTORS.map(r => (
                        <option key={r.id} value={r.id}>
                          {r.name} — {r.carbonIntensity} gCO₂e/kWh
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(15, 23, 42, 0.7)', padding: '10px 14px', borderRadius: '8px' }}>
                    <div>
                      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#f8fafc' }}>Certified 100% Green Tariff</span>
                      <p style={{ fontSize: '0.72rem', color: '#94a3b8', margin: 0 }}>Verified renewable power contract (~15 gCO₂e/kWh)</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={individualConfig.greenElectricityTariff}
                      onChange={(e) => setIndividualConfig({ ...individualConfig, greenElectricityTariff: e.target.checked })}
                      style={{ width: '18px', height: '18px', accentColor: '#00d2d3' }}
                    />
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Monthly Home Electricity</span>
                      <strong style={{ color: '#00d2d3' }}>{individualConfig.homeElectricityKwhMonthly} kWh / mo</strong>
                    </div>
                    <input
                      type="range"
                      min="100"
                      max="1200"
                      step="20"
                      value={individualConfig.homeElectricityKwhMonthly}
                      onChange={(e) => setIndividualConfig({ ...individualConfig, homeElectricityKwhMonthly: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#00d2d3' }}
                    />
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#cbd5e1', fontWeight: 600, marginBottom: '6px' }}>
                      Primary Space Heating Method
                    </label>
                    <select
                      value={individualConfig.heatingType}
                      onChange={(e) => setIndividualConfig({ ...individualConfig, heatingType: e.target.value })}
                      style={{
                        width: '100%',
                        backgroundColor: '#0f172a',
                        border: '1px solid rgba(148, 163, 184, 0.25)',
                        color: '#f8fafc',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        fontSize: '0.85rem'
                      }}
                    >
                      {Object.values(HEATING_FACTORS).map(h => (
                        <option key={h.id} value={h.id}>
                          {h.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Digital Hardware & Cloud Dev Sizing */}
                <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                  <h3 style={{ fontSize: '1rem', color: '#f8fafc', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaLaptop color="#00d2d3" />
                    <span>Digital Hardware & Cloud Dev Sizing</span>
                  </h3>

                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Laptops / Workstations Count</span>
                      <strong style={{ color: '#00d2d3' }}>{individualConfig.laptopsCount} Units</strong>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="4"
                      step="1"
                      value={individualConfig.laptopsCount}
                      onChange={(e) => setIndividualConfig({ ...individualConfig, laptopsCount: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#00d2d3' }}
                    />
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>External Displays / Monitors</span>
                      <strong style={{ color: '#00d2d3' }}>{individualConfig.monitorsCount} Screens</strong>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="4"
                      step="1"
                      value={individualConfig.monitorsCount}
                      onChange={(e) => setIndividualConfig({ ...individualConfig, monitorsCount: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#00d2d3' }}
                    />
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Device Replacement Cycle</span>
                      <strong style={{ color: '#2ecc71' }}>{individualConfig.deviceLifespanYears} Years</strong>
                    </div>
                    <input
                      type="range"
                      min="2"
                      max="6"
                      step="1"
                      value={individualConfig.deviceLifespanYears}
                      onChange={(e) => setIndividualConfig({ ...individualConfig, deviceLifespanYears: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#2ecc71' }}
                    />
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Cloud Dev Instances & API Compute</span>
                      <strong style={{ color: '#a29bfe' }}>{individualConfig.cloudMonthlyKwh} kWh / mo</strong>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="300"
                      step="10"
                      value={individualConfig.cloudMonthlyKwh}
                      onChange={(e) => setIndividualConfig({ ...individualConfig, cloudMonthlyKwh: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#a29bfe' }}
                    />
                  </div>
                </div>

                {/* Mobility & Travel */}
                <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                  <h3 style={{ fontSize: '1rem', color: '#f8fafc', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaCar color="#00d2d3" />
                    <span>Mobility, Commuting & Aviation</span>
                  </h3>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#cbd5e1', fontWeight: 600, marginBottom: '6px' }}>
                      Primary Vehicle Propulsion
                    </label>
                    <select
                      value={individualConfig.carType}
                      onChange={(e) => setIndividualConfig({ ...individualConfig, carType: e.target.value })}
                      style={{
                        width: '100%',
                        backgroundColor: '#0f172a',
                        border: '1px solid rgba(148, 163, 184, 0.25)',
                        color: '#f8fafc',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        fontSize: '0.85rem'
                      }}
                    >
                      <option value="ev_car">Electric Vehicle (EV) — 0.035 kg/km</option>
                      <option value="hybrid_car">Hybrid Vehicle — 0.105 kg/km</option>
                      <option value="petrol_car">Gasoline / Petrol Car — 0.170 kg/km</option>
                      <option value="diesel_car">Diesel Passenger Car — 0.165 kg/km</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Annual Car Driving</span>
                      <strong style={{ color: '#00d2d3' }}>{individualConfig.carAnnualKm.toLocaleString()} km / yr</strong>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="30000"
                      step="1000"
                      value={individualConfig.carAnnualKm}
                      onChange={(e) => setIndividualConfig({ ...individualConfig, carAnnualKm: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#00d2d3' }}
                    />
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Annual Rail / Train Commute</span>
                      <strong style={{ color: '#2ecc71' }}>{individualConfig.trainAnnualKm.toLocaleString()} km / yr</strong>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="20000"
                      step="500"
                      value={individualConfig.trainAnnualKm}
                      onChange={(e) => setIndividualConfig({ ...individualConfig, trainAnnualKm: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#2ecc71' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.76rem', color: '#cbd5e1', marginBottom: '4px' }}>
                        Domestic Flights / yr
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="30"
                        value={individualConfig.domesticFlightsCount}
                        onChange={(e) => setIndividualConfig({ ...individualConfig, domesticFlightsCount: Number(e.target.value) })}
                        style={{ width: '100%', backgroundColor: '#0f172a', border: '1px solid rgba(148, 163, 184, 0.25)', color: '#fff', padding: '8px', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.76rem', color: '#cbd5e1', marginBottom: '4px' }}>
                        Long-Haul Flights / yr
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="15"
                        value={individualConfig.longhaulFlightsCount}
                        onChange={(e) => setIndividualConfig({ ...individualConfig, longhaulFlightsCount: Number(e.target.value) })}
                        style={{ width: '100%', backgroundColor: '#0f172a', border: '1px solid rgba(148, 163, 184, 0.25)', color: '#fff', padding: '8px', borderRadius: '6px' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Individual Tab 3: Report */}
            {individualTab === 'report' && (
              <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h3 style={{ fontSize: '1.1rem', color: '#f8fafc', margin: '0 0 4px 0' }}>
                      Individual Annual Carbon Footprint Audit Statement (Jan – Dec)
                    </h3>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0 }}>
                      Complete Scope 1, Scope 2, and Scope 3 accounting ready for personal offsetting or client sustainability disclosures.
                    </p>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                      onClick={handleCopyReport}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: copiedReport ? 'rgba(0, 210, 211, 0.2)' : 'rgba(15, 23, 42, 0.8)',
                        border: `1px solid ${copiedReport ? '#00d2d3' : 'rgba(148, 163, 184, 0.3)'}`,
                        color: copiedReport ? '#00d2d3' : '#e2e8f0',
                        borderRadius: '8px',
                        padding: '7px 14px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      {copiedReport ? <FaCheck /> : <FaCopy />}
                      <span>{copiedReport ? 'Copied!' : 'Copy Report'}</span>
                    </button>

                    <button
                      onClick={handleDownloadReport}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'linear-gradient(135deg, #00d2d3 0%, #0abde3 100%)',
                        color: '#040d1a',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '7px 16px',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      <FaDownload />
                      <span>Download Audit (.md)</span>
                    </button>
                  </div>
                </div>

                <pre style={{
                  backgroundColor: '#030a14',
                  border: '1px solid rgba(148, 163, 184, 0.15)',
                  borderRadius: '10px',
                  padding: '20px',
                  color: '#cbd5e1',
                  fontFamily: 'monospace',
                  fontSize: '0.82rem',
                  lineHeight: '1.5',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap'
                }}>
                  {generateIndividualAnnualReport(individualConfig, individualMetrics)}
                </pre>
              </div>
            )}
          </>
        )}

        {/* ============================================================= */}
        {/* SCOPE MODE 3: MEDIUM-SIZED COMPANY (Jan - Dec)                */}
        {/* ============================================================= */}
        {scopeMode === 'company' && (
          <>
            {/* Hero Banner for Company Mode */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(40, 25, 4, 0.9) 0%, rgba(4, 15, 30, 0.95) 100%)',
              border: '1px solid rgba(248, 210, 16, 0.35)',
              borderRadius: '16px',
              padding: '24px 28px',
              marginBottom: '26px',
              boxShadow: '0 12px 30px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '20px'
            }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(248, 210, 16, 0.15)', border: '1px solid rgba(248, 210, 16, 0.35)', padding: '4px 12px', borderRadius: '20px', fontSize: '0.78rem', color: '#f8d210', fontWeight: 700, marginBottom: '8px' }}>
                  <FaBuilding size={13} />
                  <span>Medium Enterprise Carbon Accounting • CSRD & GHG Protocol Standards</span>
                </div>
                <h1 style={{ fontSize: '2.1rem', color: '#ffffff', margin: '0 0 8px 0', fontWeight: 700, letterSpacing: '-0.5px' }}>
                  Medium-Sized Company Annual Emissions (Jan – Dec)
                </h1>
                <p style={{ fontSize: '0.92rem', color: '#94a3b8', margin: 0, maxWidth: '720px', lineHeight: '1.5' }}>
                  Corporate carbon accounting engine for 20 to 250 employees modeling commercial offices, multi-cloud datacenters, corporate vehicle fleets, business flights, and teleworking emissions.
                </p>
              </div>

              {/* Company KPI Block */}
              <div style={{
                background: 'rgba(25, 18, 4, 0.9)',
                border: '1px solid rgba(248, 210, 16, 0.4)',
                borderRadius: '12px',
                padding: '18px 24px',
                textAlign: 'right',
                minWidth: '240px'
              }}>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
                  Total Corporate Footprint
                </div>
                <div style={{ fontSize: '2rem', color: '#f8d210', fontWeight: 800, fontFamily: 'monospace', margin: '4px 0' }}>
                  {companyMetrics.totals.totalAnnualTons.toFixed(2)}
                  <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 400 }}> tCO₂e / yr</span>
                </div>
                <div style={{ fontSize: '0.82rem', color: companyMetrics.totals.intensityPerFteTons <= 4.5 ? '#2ecc71' : '#ff7675', fontWeight: 700 }}>
                  {companyMetrics.totals.intensityPerFteTons.toFixed(2)} tCO₂e / FTE
                </div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px' }}>
                  Benchmark: <strong>~4.5 tCO₂e / FTE (EU Tech SME)</strong>
                </div>
              </div>
            </div>

            {/* Company Presets */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FaLightbulb color="#f8d210" size={13} />
                <span>Select Corporate Operating Profile</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
                {COMPANY_PRESETS.map(preset => {
                  const isSelected = activeCompanyPresetId === preset.id;
                  return (
                    <div
                      key={preset.id}
                      onClick={() => handleApplyCompanyPreset(preset)}
                      style={{
                        backgroundColor: isSelected ? 'rgba(248, 210, 16, 0.12)' : 'rgba(8, 20, 39, 0.65)',
                        border: `1px solid ${isSelected ? '#f8d210' : 'rgba(148, 163, 184, 0.15)'}`,
                        borderRadius: '10px',
                        padding: '12px 14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '0.88rem', fontWeight: 700, color: isSelected ? '#f8d210' : '#f8fafc' }}>
                          {preset.name}
                        </span>
                        <span style={{
                          fontSize: '0.68rem',
                          background: isSelected ? '#f8d210' : 'rgba(148, 163, 184, 0.15)',
                          color: isSelected ? '#040d1a' : '#94a3b8',
                          fontWeight: 700,
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          {preset.badge}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.76rem', color: '#94a3b8', margin: 0, lineHeight: '1.35' }}>
                        {preset.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Company Sub-Tabs Navigation */}
            <div style={{
              display: 'flex',
              gap: '8px',
              borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
              marginBottom: '24px',
              overflowX: 'auto',
              paddingBottom: '4px'
            }}>
              {[
                { id: 'trajectory', label: '12-Month Corporate Trajectory (Jan – Dec)', icon: <FaCalendarAlt /> },
                { id: 'inputs', label: 'Facilities, Fleet & IT Sizing Parameters', icon: <FaSlidersH /> },
                { id: 'report', label: 'Corporate ESG & CSRD Audit Report', icon: <FaFileAlt /> }
              ].map(tab => {
                const isActive = companyTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setCompanyTab(tab.id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: isActive ? 'rgba(248, 210, 16, 0.15)' : 'transparent',
                      border: 'none',
                      borderBottom: `2px solid ${isActive ? '#f8d210' : 'transparent'}`,
                      padding: '10px 18px',
                      color: isActive ? '#f8d210' : '#94a3b8',
                      fontSize: '0.88rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Company Tab 1: Trajectory */}
            {companyTab === 'trajectory' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* 12-Month Corporate Trajectory Visualizer */}
                <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ fontSize: '1.05rem', color: '#f8fafc', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaChartBar color="#f8d210" />
                      <span>12-Month Corporate Greenhouse Gas Trajectory (Jan – Dec)</span>
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.75rem' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#e67e22' }}>● Scope 1</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#f8d210' }}>● Scope 2</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#a29bfe' }}>● Scope 3</span>
                    </div>
                  </div>

                  {/* 12 Bar Columns */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '8px', alignItems: 'flex-end', height: '180px', paddingTop: '20px', borderBottom: '1px solid rgba(148, 163, 184, 0.2)' }}>
                    {companyMetrics.monthlyTrajectory.map(m => {
                      const maxMonthKg = (companyMetrics.totals.totalAnnualKg / 12) * 1.6;
                      const barHeight = Math.min(100, Math.max(10, (m.totalKg / maxMonthKg) * 100));
                      return (
                        <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                          <div style={{ fontSize: '0.68rem', color: '#94a3b8', marginBottom: '4px', fontFamily: 'monospace' }}>
                            {m.totalTons.toFixed(1)}t
                          </div>
                          <div style={{
                            width: '100%',
                            height: `${barHeight}%`,
                            background: 'linear-gradient(180deg, #f8d210 0%, #e67e22 100%)',
                            borderRadius: '4px 4px 0 0',
                            transition: 'height 0.3s ease'
                          }} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Month Labels */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '8px', marginTop: '8px', textAlign: 'center' }}>
                    {companyMetrics.monthlyTrajectory.map(m => (
                      <div key={m.id} style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>
                        {m.short}
                      </div>
                    ))}
                  </div>
                </div>

                {/* 12-Month Corporate Table */}
                <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 style={{ fontSize: '1rem', color: '#f8fafc', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaTable color="#f8d210" />
                      <span>Corporate Accounting Line Items by Month (kg CO₂e)</span>
                    </h3>
                    <button
                      onClick={handleDownloadCsv}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'rgba(248, 210, 16, 0.15)',
                        border: '1px solid rgba(248, 210, 16, 0.35)',
                        color: '#f8d210',
                        borderRadius: '6px',
                        padding: '5px 12px',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      <FaDownload size={11} />
                      <span>Download CSV</span>
                    </button>
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.2)', color: '#94a3b8' }}>
                          <th style={{ padding: '8px 10px' }}>Month</th>
                          <th style={{ padding: '8px 10px' }}>Season</th>
                          <th style={{ padding: '8px 10px' }}>Offices Elec</th>
                          <th style={{ padding: '8px 10px' }}>Offices Heat</th>
                          <th style={{ padding: '8px 10px' }}>Fleet Ops</th>
                          <th style={{ padding: '8px 10px' }}>Travel/Hotels</th>
                          <th style={{ padding: '8px 10px' }}>Cloud & IT</th>
                          <th style={{ padding: '8px 10px' }}>Scope 1</th>
                          <th style={{ padding: '8px 10px' }}>Scope 2</th>
                          <th style={{ padding: '8px 10px' }}>Scope 3</th>
                          <th style={{ padding: '8px 10px', color: '#f8d210' }}>Total (tCO₂e)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyMetrics.monthlyTrajectory.map((m, idx) => (
                          <tr key={m.id} style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.1)', backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(15, 23, 42, 0.4)' }}>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: '#f8fafc' }}>{m.name}</td>
                            <td style={{ padding: '8px 10px', color: '#94a3b8' }}>{m.season}</td>
                            <td style={{ padding: '8px 10px' }}>{(m.officeElectricityKg / 1000).toFixed(2)}t</td>
                            <td style={{ padding: '8px 10px' }}>{(m.officeHeatingKg / 1000).toFixed(2)}t</td>
                            <td style={{ padding: '8px 10px' }}>{(m.fleetKg / 1000).toFixed(2)}t</td>
                            <td style={{ padding: '8px 10px' }}>{(m.businessTravelKg / 1000).toFixed(2)}t</td>
                            <td style={{ padding: '8px 10px' }}>{(m.cloudAndItKg / 1000).toFixed(2)}t</td>
                            <td style={{ padding: '8px 10px', color: '#e67e22' }}>{(m.scope1 / 1000).toFixed(2)}t</td>
                            <td style={{ padding: '8px 10px', color: '#f8d210' }}>{(m.scope2 / 1000).toFixed(2)}t</td>
                            <td style={{ padding: '8px 10px', color: '#a29bfe' }}>{(m.scope3 / 1000).toFixed(2)}t</td>
                            <td style={{ padding: '8px 10px', fontWeight: 700, color: '#f8d210', fontFamily: 'monospace' }}>{m.totalTons.toFixed(2)}t</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Company Tab 2: Inputs */}
            {companyTab === 'inputs' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                {/* Workforce & Facilities */}
                <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                  <h3 style={{ fontSize: '1rem', color: '#f8fafc', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaUsers color="#f8d210" />
                    <span>Workforce & Commercial Facilities</span>
                  </h3>

                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Headcount (FTEs)</span>
                      <strong style={{ color: '#f8d210' }}>{companyConfig.employeesCount} Staff</strong>
                    </div>
                    <input
                      type="range"
                      min="15"
                      max="250"
                      step="5"
                      value={companyConfig.employeesCount}
                      onChange={(e) => setCompanyConfig({ ...companyConfig, employeesCount: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#f8d210' }}
                    />
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Remote / Distributed Working</span>
                      <strong style={{ color: '#00d2d3' }}>{companyConfig.remoteWorkPercent}% Remote</strong>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={companyConfig.remoteWorkPercent}
                      onChange={(e) => setCompanyConfig({ ...companyConfig, remoteWorkPercent: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#00d2d3' }}
                    />
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Office Floor Space</span>
                      <strong style={{ color: '#f8d210' }}>{companyConfig.officeSqM.toLocaleString()} m²</strong>
                    </div>
                    <input
                      type="range"
                      min="100"
                      max="5000"
                      step="50"
                      value={companyConfig.officeSqM}
                      onChange={(e) => setCompanyConfig({ ...companyConfig, officeSqM: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#f8d210' }}
                    />
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#cbd5e1', fontWeight: 600, marginBottom: '6px' }}>
                      Headquarters Region (Grid Carbon Factor)
                    </label>
                    <select
                      value={companyConfig.region}
                      onChange={(e) => setCompanyConfig({ ...companyConfig, region: e.target.value })}
                      style={{
                        width: '100%',
                        backgroundColor: '#0f172a',
                        border: '1px solid rgba(148, 163, 184, 0.25)',
                        color: '#f8fafc',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        fontSize: '0.85rem'
                      }}
                    >
                      {GRID_CARBON_FACTORS.map(r => (
                        <option key={r.id} value={r.id}>
                          {r.name} — {r.carbonIntensity} gCO₂e/kWh
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Cloud & Corporate IT */}
                <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                  <h3 style={{ fontSize: '1rem', color: '#f8fafc', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaServer color="#f8d210" />
                    <span>Cloud Architecture & On-Prem Racks</span>
                  </h3>

                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Active Cloud Compute Instances</span>
                      <strong style={{ color: '#f8d210' }}>{companyConfig.cloudInstancesCount} VMs</strong>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="500"
                      step="10"
                      value={companyConfig.cloudInstancesCount}
                      onChange={(e) => setCompanyConfig({ ...companyConfig, cloudInstancesCount: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#f8d210' }}
                    />
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>On-Premises Server Racks</span>
                      <strong style={{ color: '#2ecc71' }}>{companyConfig.onpremServerRacks} Racks</strong>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="12"
                      step="1"
                      value={companyConfig.onpremServerRacks}
                      onChange={(e) => setCompanyConfig({ ...companyConfig, onpremServerRacks: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#2ecc71' }}
                    />
                  </div>

                  <div style={{ marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(15, 23, 42, 0.7)', padding: '10px 14px', borderRadius: '8px' }}>
                    <div>
                      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#f8fafc' }}>Corporate Green PPA Tariff</span>
                      <p style={{ fontSize: '0.72rem', color: '#94a3b8', margin: 0 }}>Verified renewable contract for offices</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={companyConfig.greenTariff}
                      onChange={(e) => setCompanyConfig({ ...companyConfig, greenTariff: e.target.checked })}
                      style={{ width: '18px', height: '18px', accentColor: '#f8d210' }}
                    />
                  </div>
                </div>

                {/* Fleet & Business Travel */}
                <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '20px' }}>
                  <h3 style={{ fontSize: '1rem', color: '#f8fafc', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaPlane color="#f8d210" />
                    <span>Corporate Fleet & Commercial Aviation</span>
                  </h3>

                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      <span>Fleet Total Mileage</span>
                      <strong style={{ color: '#f8d210' }}>{companyConfig.fleetAnnualKmTotal.toLocaleString()} km / yr</strong>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="500000"
                      step="10000"
                      value={companyConfig.fleetAnnualKmTotal}
                      onChange={(e) => setCompanyConfig({ ...companyConfig, fleetAnnualKmTotal: Number(e.target.value) })}
                      style={{ width: '100%', accentColor: '#f8d210' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.76rem', color: '#cbd5e1', marginBottom: '4px' }}>
                        Domestic Flights / yr
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="500"
                        value={companyConfig.annualDomesticFlights}
                        onChange={(e) => setCompanyConfig({ ...companyConfig, annualDomesticFlights: Number(e.target.value) })}
                        style={{ width: '100%', backgroundColor: '#0f172a', border: '1px solid rgba(148, 163, 184, 0.25)', color: '#fff', padding: '8px', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.76rem', color: '#cbd5e1', marginBottom: '4px' }}>
                        Long-Haul Flights / yr
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="200"
                        value={companyConfig.annualLonghaulFlights}
                        onChange={(e) => setCompanyConfig({ ...companyConfig, annualLonghaulFlights: Number(e.target.value) })}
                        style={{ width: '100%', backgroundColor: '#0f172a', border: '1px solid rgba(148, 163, 184, 0.25)', color: '#fff', padding: '8px', borderRadius: '6px' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.76rem', color: '#cbd5e1', marginBottom: '4px' }}>
                      Hotel Room-Nights / yr
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="2000"
                      value={companyConfig.annualHotelNights}
                      onChange={(e) => setCompanyConfig({ ...companyConfig, annualHotelNights: Number(e.target.value) })}
                      style={{ width: '100%', backgroundColor: '#0f172a', border: '1px solid rgba(148, 163, 184, 0.25)', color: '#fff', padding: '8px', borderRadius: '6px' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Company Tab 3: Report */}
            {companyTab === 'report' && (
              <div style={{ background: 'rgba(8, 20, 39, 0.75)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '14px', padding: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h3 style={{ fontSize: '1.1rem', color: '#f8fafc', margin: '0 0 4px 0' }}>
                      Corporate ESG Sustainability & CSRD Audit Statement (Jan – Dec)
                    </h3>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0 }}>
                      Complete Scope 1, Scope 2, and Scope 3 disclosures aligned with ESRS E1 Climate Change standard.
                    </p>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                      onClick={handleCopyReport}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: copiedReport ? 'rgba(248, 210, 16, 0.2)' : 'rgba(15, 23, 42, 0.8)',
                        border: `1px solid ${copiedReport ? '#f8d210' : 'rgba(148, 163, 184, 0.3)'}`,
                        color: copiedReport ? '#f8d210' : '#e2e8f0',
                        borderRadius: '8px',
                        padding: '7px 14px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      {copiedReport ? <FaCheck /> : <FaCopy />}
                      <span>{copiedReport ? 'Copied!' : 'Copy Report'}</span>
                    </button>

                    <button
                      onClick={handleDownloadReport}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'linear-gradient(135deg, #f8d210 0%, #f39c12 100%)',
                        color: '#040d1a',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '7px 16px',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      <FaDownload />
                      <span>Download CSRD (.md)</span>
                    </button>
                  </div>
                </div>

                <pre style={{
                  backgroundColor: '#030a14',
                  border: '1px solid rgba(148, 163, 184, 0.15)',
                  borderRadius: '10px',
                  padding: '20px',
                  color: '#cbd5e1',
                  fontFamily: 'monospace',
                  fontSize: '0.82rem',
                  lineHeight: '1.5',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap'
                }}>
                  {generateCompanyAnnualReport(companyConfig, companyMetrics)}
                </pre>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default GreenOpsEstimatorPage;
